/**
 * Сервис для уведомлений пользователя об ошибках
 * 
 * Предоставляет единый интерфейс для показа ошибок пользователю
 * с поддержкой разных типов уведомлений
 */

import logger from '../logger/logger';
import i18n from '../../i18n';

/**
 * Типы уведомлений
 */
export const NOTIFICATION_TYPES = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success',
};

class ErrorNotificationService {
  constructor() {
    this.queue = [];
    this.maxQueueSize = 5;
  }

  /**
   * Показать уведомление пользователю
   * Можно расширить для интеграции с Toast библиотекой или Redux state
   */
  show(message, type = NOTIFICATION_TYPES.ERROR, duration = 5000) {
    logger.warn(`User notification: [${type}] ${message}`);
    
    // В будущем можно интегрировать с Toast библиотекой
    // Или с Redux для глобальных уведомлений
    
    // Временно используем alert для критических ошибок
    if (type === NOTIFICATION_TYPES.ERROR) {
      // В Telegram WebApp можно использовать window.Telegram.WebApp.showAlert
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(message);
      } else {
        // Fallback на стандартный alert
        alert(message);
      }
    } else {
      // Для остальных типов можно использовать snackbar/toast
      console.warn(`[Notification ${type}]: ${message}`);
    }

    // Добавляем в очередь для возможной интеграции с UI
    this.addToQueue({ message, type, duration, timestamp: Date.now() });
  }

  /**
   * Добавить уведомление в очередь
   */
  addToQueue(notification) {
    if (this.queue.length >= this.maxQueueSize) {
      this.queue.shift(); // Удаляем старое уведомление
    }
    this.queue.push(notification);
  }

  /**
   * Показать ошибку API пользователю
   */
  showApiError(error) {
    let message = i18n.t('error.generic', 'An error occurred. Please try again.');
    
    const status = error.response?.status;
    
    switch (status) {
      case 401:
        message = i18n.t('error.session_expired', 'Session expired. The page will refresh.');
        // Можно автоматически перезагрузить страницу через 2 секунды
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        break;
      case 403:
        message = i18n.t('error.access_denied', "Access denied. You don't have permission.");
        break;
      case 404:
        message = i18n.t('error.not_found', 'Resource not found.');
        break;
      case 429:
        message = i18n.t('error.too_many_requests', 'Too many requests. Please wait a moment.');
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        message = i18n.t('error.server', 'Server error. Please try again later.');
        break;
      default:
        if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.message) {
          message = error.message;
        }
    }
    
    this.show(message, NOTIFICATION_TYPES.ERROR);
  }

  /**
   * Показать сетевую ошибку
   */
  showNetworkError(error) {
    const message = i18n.t('error.network', 'Network error. Please check your connection and try again.');
    this.show(message, NOTIFICATION_TYPES.ERROR);
  }

  /**
   * Показать успешное уведомление
   */
  showSuccess(message) {
    this.show(message, NOTIFICATION_TYPES.SUCCESS, 3000);
  }

  /**
   * Показать предупреждение
   */
  showWarning(message) {
    this.show(message, NOTIFICATION_TYPES.WARNING);
  }

  /**
   * Показать информационное сообщение
   */
  showInfo(message) {
    this.show(message, NOTIFICATION_TYPES.INFO);
  }

  /**
   * Получить очередь уведомлений
   */
  getQueue() {
    return [...this.queue];
  }

  /**
   * Очистить очередь
   */
  clearQueue() {
    this.queue = [];
  }
}

// Создаем единственный экземпляр сервиса
export const errorNotification = new ErrorNotificationService();

// Экспортируем по умолчанию
export default errorNotification;

